import { useState, useEffect } from 'react';
import { adminAPI } from '../../lib/api';
import { Save, Loader2, Shield, BarChart3, Code, Globe, RefreshCw, CheckCircle, XCircle, Upload, Image as ImageIcon, Palette } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { toast } from 'sonner';
import { useRef } from 'react';

export default function SettingsPage() {
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sitemapStatus, setSitemapStatus] = useState(null);
  const [pinging, setPinging] = useState(false);
  const [branding, setBranding] = useState({ favicon: {}, logo: {}, site_title: '', site_description: '' });
  const [uploadingFavicon, setUploadingFavicon] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const faviconRef = useRef(null);
  const logoRef = useRef(null);

  useEffect(() => {
    Promise.all([
      adminAPI.getSettings(),
      adminAPI.getSitemapStatus(),
      adminAPI.getBranding()
    ]).then(([settingsRes, sitemapRes, brandingRes]) => {
      const map = {};
      settingsRes.data.forEach(s => { map[s.key] = s.value; });
      setSettings(map);
      setSitemapStatus(sitemapRes.data);
      setBranding(brandingRes.data);
    }).finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await adminAPI.updateSettings(settings);
      toast.success('Ayarlar kaydedildi');
    } catch { toast.error('Kaydedilemedi'); }
    finally { setSaving(false); }
  };

  const handlePingSitemap = async () => {
    setPinging(true);
    try {
      const res = await adminAPI.pingSitemap();
      setSitemapStatus(prev => ({
        ...prev,
        last_ping: res.data.last_ping,
        ping_results: res.data.ping_results
      }));
      toast.success('Sitemap ping gonderildi');
    } catch { toast.error('Ping gonderilemedi'); }
    finally { setPinging(false); }
  };

  const handleFaviconUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadingFavicon(true);
    try {
      const formData = new FormData();
      formData.append('dosya', file);
      const res = await adminAPI.uploadFavicon(formData);
      setBranding(prev => ({ ...prev, favicon: { files: res.data.files, version: res.data.version } }));
      toast.success('Favicon guncellendi');
    } catch (err) { toast.error(err.response?.data?.detail || 'Favicon yuklenemedi'); }
    finally { setUploadingFavicon(false); if (faviconRef.current) faviconRef.current.value = ''; }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadingLogo(true);
    try {
      const formData = new FormData();
      formData.append('dosya', file);
      const res = await adminAPI.uploadLogo(formData);
      setBranding(prev => ({ ...prev, logo: { url: res.data.url, version: res.data.version } }));
      toast.success('Logo guncellendi');
    } catch (err) { toast.error(err.response?.data?.detail || 'Logo yuklenemedi'); }
    finally { setUploadingLogo(false); if (logoRef.current) logoRef.current.value = ''; }
  };

  const handleSaveBrandingText = async () => {
    setSaving(true);
    try {
      await adminAPI.updateSettings({
        site_title: branding.site_title || '',
        site_description: branding.site_description || ''
      });
      toast.success('Site basligi kaydedildi');
    } catch { toast.error('Kaydedilemedi'); }
    finally { setSaving(false); }
  };

  const BACKEND = '';

  if (loading) return <div className="flex items-center justify-center h-32"><Loader2 className="h-6 w-6 animate-spin" /></div>;

  return (
    <div data-testid="admin-settings">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-semibold">Ayarlar</h1>
        <Button onClick={handleSave} disabled={saving} data-testid="settings-save">
          {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
          Kaydet
        </Button>
      </div>

      {/* Site Branding */}
      <Card className="mb-6" data-testid="branding-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><Palette className="h-5 w-5" /> Site Branding</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Favicon */}
          <div className="border rounded-lg p-4 space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-base font-semibold">Favicon</Label>
                <p className="text-xs text-muted-foreground mt-0.5">PNG, SVG veya ICO. Maks. 1MB. Tum boyutlar otomatik olusturulur.</p>
              </div>
              <div>
                <input ref={faviconRef} type="file" accept=".png,.svg,.ico" onChange={handleFaviconUpload} className="hidden" />
                <Button variant="outline" size="sm" onClick={() => faviconRef.current?.click()} disabled={uploadingFavicon} data-testid="upload-favicon-btn">
                  {uploadingFavicon ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Upload className="h-4 w-4 mr-2" />}
                  Favicon Yukle
                </Button>
              </div>
            </div>
            {branding.favicon?.files && Object.keys(branding.favicon.files).length > 0 && (
              <div className="flex items-center gap-3 flex-wrap" data-testid="favicon-preview">
                {['16', '32', '48', '96'].map(size => {
                  const url = branding.favicon.files[size];
                  return url ? (
                    <div key={size} className="text-center">
                      <img src={`${BACKEND}${url}`} alt={`${size}x${size}`} className="border rounded" style={{ width: `${Math.min(parseInt(size), 48)}px`, height: `${Math.min(parseInt(size), 48)}px` }} />
                      <p className="text-xs text-muted-foreground mt-1">{size}px</p>
                    </div>
                  ) : null;
                })}
                <div className="text-xs text-muted-foreground ml-2">
                  +180 (Apple) +192 +512 (Android/PWA)
                </div>
              </div>
            )}
          </div>

          {/* Logo */}
          <div className="border rounded-lg p-4 space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-base font-semibold">Site Logo</Label>
                <p className="text-xs text-muted-foreground mt-0.5">PNG veya SVG. Maks. 2MB. Header ve footer'da kullanilir.</p>
              </div>
              <div>
                <input ref={logoRef} type="file" accept=".png,.svg" onChange={handleLogoUpload} className="hidden" />
                <Button variant="outline" size="sm" onClick={() => logoRef.current?.click()} disabled={uploadingLogo} data-testid="upload-logo-btn">
                  {uploadingLogo ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Upload className="h-4 w-4 mr-2" />}
                  Logo Yukle
                </Button>
              </div>
            </div>
            {branding.logo?.url && (
              <div className="flex items-center gap-4" data-testid="logo-preview">
                <img src={`${BACKEND}${branding.logo.url}`} alt="Site logo" className="h-10 w-auto object-contain border rounded p-1" />
                <span className="text-xs text-muted-foreground">Mevcut logo</span>
              </div>
            )}
          </div>

          {/* Site Title & Description */}
          <div className="border rounded-lg p-4 space-y-3">
            <Label className="text-base font-semibold">Site Basligi ve Aciklamasi</Label>
            <div>
              <Label className="text-xs">Site Basligi</Label>
              <Input
                value={branding.site_title || ''}
                onChange={e => setBranding(prev => ({ ...prev, site_title: e.target.value }))}
                placeholder="Uretim Noktasi"
                data-testid="site-title-input"
              />
            </div>
            <div>
              <Label className="text-xs">Site Aciklamasi</Label>
              <textarea
                value={branding.site_description || ''}
                onChange={e => setBranding(prev => ({ ...prev, site_description: e.target.value }))}
                rows={2}
                className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                placeholder="Turkiye genelinde endustriyel uretim hizmetleri..."
                data-testid="site-description-input"
              />
            </div>
            <Button size="sm" onClick={handleSaveBrandingText} disabled={saving} data-testid="save-branding-text">
              <Save className="h-4 w-4 mr-2" /> Kaydet
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Sitemap Status */}
      {sitemapStatus && (
        <Card className="mb-6" data-testid="sitemap-status-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><Globe className="h-5 w-5" /> Sitemap Durumu</CardTitle>
          </CardHeader>
          <CardContent>
            {/* Page counts */}
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-5">
              <div className="text-center p-3 bg-slate-50 rounded-lg">
                <p className="text-2xl font-semibold tabular-nums">{sitemapStatus.indexed_pages?.landing_pages || 0}</p>
                <p className="text-xs text-muted-foreground">Landing Page</p>
              </div>
              <div className="text-center p-3 bg-slate-50 rounded-lg">
                <p className="text-2xl font-semibold tabular-nums">{sitemapStatus.indexed_pages?.blog_posts || 0}</p>
                <p className="text-xs text-muted-foreground">Blog Yazisi</p>
              </div>
              <div className="text-center p-3 bg-slate-50 rounded-lg">
                <p className="text-2xl font-semibold tabular-nums">{sitemapStatus.indexed_pages?.directory_companies || 0}</p>
                <p className="text-xs text-muted-foreground">Rehber Firma</p>
              </div>
              <div className="text-center p-3 bg-slate-50 rounded-lg">
                <p className="text-2xl font-semibold tabular-nums">{sitemapStatus.indexed_pages?.cities || 0}</p>
                <p className="text-xs text-muted-foreground">Sehir</p>
              </div>
            </div>

            {/* Last ping info + action */}
            <div className="flex items-start justify-between border-t pt-4 gap-4">
              <div className="space-y-1.5">
                <div className="flex items-center gap-2">
                  <p className="text-sm">
                    <span className="text-muted-foreground">Son Ping:</span>{' '}
                    <span className="font-medium tabular-nums" data-testid="last-ping-time">
                      {sitemapStatus.last_ping
                        ? new Date(sitemapStatus.last_ping).toLocaleString('tr-TR')
                        : 'Henuz ping gonderilmedi'}
                    </span>
                  </p>
                  {sitemapStatus.overall_status && (
                    <span className={`inline-flex items-center text-xs px-2 py-0.5 rounded-full font-medium ${
                      sitemapStatus.overall_status === 'success' ? 'bg-emerald-100 text-emerald-800' :
                      sitemapStatus.overall_status === 'partial' ? 'bg-amber-100 text-amber-800' :
                      'bg-red-100 text-red-800'
                    }`} data-testid="ping-status-badge">
                      {sitemapStatus.overall_status === 'success' ? 'Basarili' :
                       sitemapStatus.overall_status === 'partial' ? 'Kismi' : 'Basarisiz'}
                    </span>
                  )}
                </div>
                {sitemapStatus.ping_results && sitemapStatus.ping_results.length > 0 && (
                  <div className="flex gap-3">
                    {sitemapStatus.ping_results.map((pr, i) => (
                      <span key={i} className="flex items-center gap-1 text-xs" data-testid={`ping-result-${pr.engine?.toLowerCase()}`}>
                        {pr.success
                          ? <CheckCircle className="h-3.5 w-3.5 text-emerald-600" />
                          : <XCircle className="h-3.5 w-3.5 text-red-500" />
                        }
                        <span className="font-medium">{pr.engine}</span>
                        <span className="text-muted-foreground">HTTP {pr.status}</span>
                      </span>
                    ))}
                  </div>
                )}
                {sitemapStatus.trigger && (
                  <p className="text-xs text-muted-foreground">
                    Tetikleyen: <span className="font-mono">{sitemapStatus.trigger}</span>
                  </p>
                )}
              </div>
              <Button variant="outline" size="sm" onClick={handlePingSitemap} disabled={pinging} data-testid="ping-sitemap-btn">
                <RefreshCw className={`h-4 w-4 mr-2 ${pinging ? 'animate-spin' : ''}`} />
                {pinging ? 'Gonderiliyor...' : 'Sitemap Ping'}
              </Button>
            </div>

            {/* Recent ping history */}
            {sitemapStatus.recent_pings && sitemapStatus.recent_pings.length > 0 && (
              <div className="mt-4 pt-4 border-t">
                <p className="text-xs font-medium text-muted-foreground mb-2">Son Ping Gecmisi</p>
                <div className="space-y-1.5">
                  {sitemapStatus.recent_pings.map((rp, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs" data-testid={`ping-history-${i}`}>
                      <span className="tabular-nums text-muted-foreground w-36 flex-shrink-0">
                        {new Date(rp.created_at).toLocaleString('tr-TR')}
                      </span>
                      <span className="font-mono text-muted-foreground">{rp.details}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><Shield className="h-5 w-5" /> hCaptcha Ayarlari</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>hCaptcha Site Key</Label>
            <Input
              value={settings.hcaptcha_site_key || ''}
              onChange={e => setSettings(s => ({...s, hcaptcha_site_key: e.target.value}))}
              placeholder="hCaptcha site key girin"
              data-testid="hcaptcha-site-key"
            />
          </div>
          <div>
            <Label>hCaptcha Secret Key</Label>
            <Input
              value={settings.hcaptcha_secret || ''}
              onChange={e => setSettings(s => ({...s, hcaptcha_secret: e.target.value}))}
              placeholder="hCaptcha secret key girin"
              type="password"
              data-testid="hcaptcha-secret-key"
            />
          </div>
          <p className="text-xs text-muted-foreground">
            hCaptcha anahtarlari bos birakilirsa, teklif formunda captcha dogrulamasi yapilmaz.
          </p>
        </CardContent>
      </Card>

      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><BarChart3 className="h-5 w-5" /> SEO ve Analytics Ayarlari</CardTitle>
        </CardHeader>
        <CardContent className="space-y-5">
          <div>
            <Label>Google Analytics Measurement ID</Label>
            <Input
              value={settings.ga_measurement_id || ''}
              onChange={e => setSettings(s => ({...s, ga_measurement_id: e.target.value}))}
              placeholder="G-XXXXXXXXXX"
              data-testid="ga-measurement-id"
            />
            <p className="text-xs text-muted-foreground mt-1">Google Analytics 4 olcum kimligini girin. Bos birakilirsa GA yuklenmez.</p>
          </div>
          <div>
            <Label>Google Tag Manager Container ID</Label>
            <Input
              value={settings.gtm_container_id || ''}
              onChange={e => setSettings(s => ({...s, gtm_container_id: e.target.value}))}
              placeholder="GTM-XXXXXXX"
              data-testid="gtm-container-id"
            />
            <p className="text-xs text-muted-foreground mt-1">GTM container kimligini girin. Bos birakilirsa GTM yuklenmez.</p>
          </div>
          <div>
            <Label>Google Search Console Dogrulama Kodu</Label>
            <Input
              value={settings.search_console_code || ''}
              onChange={e => setSettings(s => ({...s, search_console_code: e.target.value}))}
              placeholder="Dogrulama kodu (meta tag icerigi)"
              data-testid="search-console-code"
            />
            <p className="text-xs text-muted-foreground mt-1">Search Console meta tag dogrulama kodunu girin. Sayfaya otomatik eklenir.</p>
          </div>
          <div>
            <Label>Meta Pixel ID (Opsiyonel)</Label>
            <Input
              value={settings.meta_pixel_id || ''}
              onChange={e => setSettings(s => ({...s, meta_pixel_id: e.target.value}))}
              placeholder="XXXXXXXXXXXXXXX"
              data-testid="meta-pixel-id"
            />
            <p className="text-xs text-muted-foreground mt-1">Facebook/Meta Pixel kimligini girin. Bos birakilirsa yuklenmez.</p>
          </div>
          <div>
            <Label className="flex items-center gap-2"><Code className="h-4 w-4" /> Ozel Head Script</Label>
            <textarea
              value={settings.head_scripts || ''}
              onChange={e => setSettings(s => ({...s, head_scripts: e.target.value}))}
              rows={4}
              className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm font-mono"
              placeholder="<!-- Ozel script kodlari buraya -->"
              data-testid="head-scripts"
            />
            <p className="text-xs text-muted-foreground mt-1">Tum public sayfalarin &lt;head&gt; etiketine enjekte edilecek ozel script kodlari. Dikkatli kullanin.</p>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Dosya Yukleme Ayarlari</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>Maksimum Dosya Boyutu (MB)</Label>
            <Input
              value={settings.max_upload_mb || '100'}
              onChange={e => setSettings(s => ({...s, max_upload_mb: e.target.value}))}
              type="number"
            />
          </div>
        </CardContent>
      </Card>

      <Card className="mt-6">
        <CardHeader>
          <CardTitle>E-posta Bildirim Ayarlari (SMTP)</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label>SMTP Sunucu</Label>
              <Input
                value={settings.smtp_host || ''}
                onChange={e => setSettings(s => ({...s, smtp_host: e.target.value}))}
                placeholder="smtp.gmail.com"
              />
            </div>
            <div>
              <Label>SMTP Port</Label>
              <Input
                value={settings.smtp_port || '587'}
                onChange={e => setSettings(s => ({...s, smtp_port: e.target.value}))}
                type="number"
              />
            </div>
            <div>
              <Label>SMTP Kullanici</Label>
              <Input
                value={settings.smtp_user || ''}
                onChange={e => setSettings(s => ({...s, smtp_user: e.target.value}))}
              />
            </div>
            <div>
              <Label>SMTP Sifre</Label>
              <Input
                value={settings.smtp_pass || ''}
                onChange={e => setSettings(s => ({...s, smtp_pass: e.target.value}))}
                type="password"
              />
            </div>
            <div>
              <Label>Gonderen E-posta</Label>
              <Input
                value={settings.smtp_from || ''}
                onChange={e => setSettings(s => ({...s, smtp_from: e.target.value}))}
                placeholder="noreply@industrialhub.com"
              />
            </div>
            <div>
              <Label>Bildirim E-posta Adresi</Label>
              <Input
                value={settings.admin_notify_email || ''}
                onChange={e => setSettings(s => ({...s, admin_notify_email: e.target.value}))}
                placeholder="admin@industrialhub.com"
              />
            </div>
          </div>
          <p className="text-xs text-muted-foreground">
            SMTP ayarlari yapildiginda, yeni teklif talebi geldiginde bildirim e-postasi gonderilir.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
